import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ProductPricing } from '@galaxy/billing';
import { retryer } from '@vendasta/rx-utils';
import { BilledProduct } from '@vendasta/shared';
import { ResellerItem } from '@vendasta/store';
import { forkJoin as observableForkJoin, Observable, of as observableOf, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { pricingOnlyBilledProduct } from '../billing/pricing-adapter';
import { PricingService } from '../billing/pricing.service';
import { ResellerItemService } from './reseller-item.service';
import { chunk } from './utils';

@Injectable({ providedIn: 'root' })
export class BilledProductService {
  resellerItems$: Observable<ResellerItem[]>;
  billedProductsLoaded: Map<string, boolean> = new Map();
  retailProductsLoaded: Map<string, boolean> = new Map();
  private billedProductMap$: Observable<Map<string, BilledProduct>>;
  private _billedProducts$: Observable<BilledProduct[]>;
  private retailProductMap$: Observable<Map<string, BilledProduct>>;
  private _retailProducts$: Observable<BilledProduct[]>;

  private errorStrategy$ = retryer({
    maxAttempts: 3,
    retryDelay: 200,
    timeoutMilliseconds: 10000,
    customRetryChecker: (errResp: HttpErrorResponse) => errResp.status === 503 || errResp.status === 504,
  });

  constructor(
    private resellerItemService: ResellerItemService,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
    private pricingService: PricingService,
  ) {
    this.resellerItems$ = this.resellerItemService.getPartnerData().pipe(shareReplay(1));

    const products$ = this.resellerItems$.pipe(
      filter((resellerItems) => Boolean(resellerItems) && resellerItems.length > 0),
      map((resellerItems) => {
        return (resellerItems || []).map((item) => item.billingId);
      }),
      map((skus) => {
        if (!skus || skus.length === 0) {
          return [];
        }
        return chunk(skus, 100);
      }),
      withLatestFrom(partnerId$),
    );
    this._billedProducts$ = products$.pipe(
      switchMap(([skuChunks, partnerId]) => {
        if (!skuChunks || skuChunks.length === 0) {
          return observableOf([]);
        }
        const calls: Observable<BilledProduct[]>[] = skuChunks.map((thisChunk) => {
          return this.pricingService.getMultiProductPricing(partnerId, thisChunk).pipe(
            this.errorStrategy$,
            map((pricingMap: { [sku: string]: ProductPricing }) => {
              const billedProducts = [];
              const keys = Object.keys(pricingMap);
              keys.forEach((key) => {
                if (pricingMap[key]) {
                  billedProducts.push(pricingOnlyBilledProduct(key, pricingMap[key]));
                  this.billedProductsLoaded.set(key, true);
                }
              });
              return billedProducts;
            }),
          ) as Observable<BilledProduct[]>;
        });
        return observableForkJoin(calls);
      }),
      map(this.flattenedBilledProducts),
      map(this.filterBilledProducts),
      catchError(this.handleError),
      shareReplay(1),
    );

    this.billedProductMap$ = this._billedProducts$.pipe(
      map((billedProducts) => {
        const billedProductMap = new Map();
        billedProducts.forEach((billedProduct) => {
          if (billedProduct) {
            billedProductMap.set(billedProduct.productId, billedProduct);
          }
        });
        return billedProductMap;
      }),
      shareReplay(1),
    );
    this._retailProducts$ = products$.pipe(
      switchMap(([skuChunks, partnerId]) => {
        if (!skuChunks || skuChunks.length === 0) {
          return observableOf([]);
        }
        const calls: Observable<BilledProduct[]>[] = skuChunks.map((thisChunk) => {
          return this.pricingService.getMultiRetailPricing(partnerId, thisChunk).pipe(
            this.errorStrategy$,
            map((pricingMap: { [sku: string]: ProductPricing }) => {
              const retailProducts = [];
              const keys = Object.keys(pricingMap);
              keys.forEach((key) => {
                if (pricingMap[key]) {
                  retailProducts.push(pricingOnlyBilledProduct(key, pricingMap[key]));
                  this.retailProductsLoaded.set(key, true);
                }
              });
              return retailProducts;
            }),
          ) as Observable<BilledProduct[]>;
        });
        return observableForkJoin(calls);
      }),
      map(this.flattenedBilledProducts),
      map(this.filterBilledProducts),
      catchError(this.handleError),
      shareReplay(1),
    );
    this.retailProductMap$ = this._retailProducts$.pipe(
      map((retailProducts) => {
        const retailProductMap = new Map();
        retailProducts.forEach((retailProduct) => {
          if (retailProduct) {
            retailProductMap.set(retailProduct.productId, retailProduct);
          }
        });
        return retailProductMap;
      }),
      shareReplay(1),
    );
  }
  private flattenedBilledProducts(results: BilledProduct[][]): BilledProduct[] {
    const flatProducts: BilledProduct[] = [];
    results.forEach((bps) => {
      flatProducts.push(...bps);
    });
    return flatProducts;
  }
  private filterBilledProducts(products: BilledProduct[]): BilledProduct[] {
    if (!products) {
      return [];
    }
    return products.filter(Boolean);
  }
  private handleError(err: any): Observable<never> {
    console.error(err);
    return throwError(err);
  }
  getBillableProduct(sku: string): Observable<BilledProduct> {
    if (!sku) {
      return observableOf(undefined);
    }
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.pricingService.getProductPricing(partnerId, sku)),
      map((pricingMap: ProductPricing) => {
        if (pricingMap) {
          return pricingOnlyBilledProduct(sku, pricingMap);
        }
        return undefined;
      }),
      shareReplay(1),
    );
  }

  getBillableProducts(skus: string[]): Observable<BilledProduct[]> {
    if (!skus || skus.length === 0) {
      return observableOf([]);
    }
    return this.billedProductMap$.pipe(
      map((billedProductMap) => {
        return skus.map((sku) => {
          return billedProductMap.get(sku);
        });
      }),
    );
  }
  getRetailProducts(skus: string[]): Observable<BilledProduct[]> {
    if (!skus || skus.length === 0) {
      return observableOf([]);
    }
    return this.retailProductMap$.pipe(
      map((retailProductMap) => {
        return skus.map((sku) => {
          return retailProductMap.get(sku);
        });
      }),
    );
  }
}
